import { FocusTrap, FocusTrapFactory } from "@angular/cdk/a11y";
import {
  GlobalPositionStrategy,
  Overlay,
  OverlayConfig,
  OverlayRef,
} from "@angular/cdk/overlay";
import { CdkPortalOutlet, ComponentPortal, ComponentType } from "@angular/cdk/portal";
import {
  AfterViewInit,
  Component,
  ComponentRef,
  ElementRef,
  Injectable,
  InjectionToken,
  Injector,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { Observable, Subject } from "rxjs";

/**
 * Injection token mirroring Material's MAT_DIALOG_DATA — gives a dialog
 * component access to the `data` passed in BvDialogConfig.
 */
export const BV_DIALOG_DATA = new InjectionToken<unknown>("BvDialogData");

export interface BvDialogPosition {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
}

/** Subset of MatDialogConfig actually used across the app. */
export interface BvDialogConfig<D = unknown> {
  data?: D;
  width?: string;
  height?: string;
  minWidth?: string;
  maxWidth?: string;
  maxHeight?: string;
  panelClass?: string | string[];
  backdropClass?: string | string[];
  hasBackdrop?: boolean;
  disableClose?: boolean;
  /** Accepted for parity; the container always traps focus. */
  autoFocus?: boolean;
  ariaLabel?: string;
  position?: BvDialogPosition;
}

/** Handle to an open dialog. Mirrors the slice of MatDialogRef the app uses. */
export class BvDialogRef<T = any, R = any> {
  componentInstance: T | null = null;

  private readonly _afterClosed = new Subject<R | undefined>();

  constructor(private readonly overlayRef: OverlayRef) {}

  close(result?: R): void {
    this.overlayRef.dispose();
    this._afterClosed.next(result);
    this._afterClosed.complete();
  }

  afterClosed(): Observable<R | undefined> {
    return this._afterClosed.asObservable();
  }
}

/**
 * Overlay shell that renders the dialog component via a portal outlet and
 * provides the surface chrome + focus trap. Not used directly — the service
 * creates it.
 */
@Component({
    selector: "bv-dialog-container",
    template: `<ng-template cdkPortalOutlet></ng-template>`,
    host: {
        role: "dialog",
        "aria-modal": "true",
        tabindex: "-1",
        "[attr.aria-label]": "ariaLabel || null",
    },
    styles: [
        `
      :host {
        display: flex;
        flex-direction: column;
        width: 100%;
        max-height: 100%;
        background: var(--bg-surface-alt);
        color: var(--text-secondary);
        border: 1px solid var(--bg-separator);
        border-radius: var(--radius-2xl);
        box-shadow: var(--shadow-lg);
        font-family: var(--font-sans);
        overflow: hidden;
      }
    `,
    ],
    imports: [CdkPortalOutlet]
})
export class BvDialogContainerComponent implements AfterViewInit, OnDestroy {
  ariaLabel?: string;
  autoFocus = true;

  @ViewChild(CdkPortalOutlet, { static: true })
  private portalOutlet!: CdkPortalOutlet;

  private focusTrap?: FocusTrap;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly focusTrapFactory: FocusTrapFactory
  ) {}

  attachComponentPortal<C>(portal: ComponentPortal<C>): ComponentRef<C> {
    return this.portalOutlet.attachComponentPortal(portal);
  }

  ngAfterViewInit(): void {
    this.focusTrap = this.focusTrapFactory.create(this.elementRef.nativeElement);
    if (this.autoFocus) {
      this.focusTrap.focusInitialElementWhenReady();
    } else {
      // Keep focus inside the dialog for a11y, but don't focus an input —
      // that would scroll a long dialog to it (e.g. the repeat-tournament
      // preview rows). Focus the container itself instead.
      this.elementRef.nativeElement.focus();
    }
  }

  ngOnDestroy(): void {
    this.focusTrap?.destroy();
  }
}

/**
 * Programmatic dialog service mirroring MatDialog.open / closeAll. Renders
 * a component into a centered CDK overlay with the bv-dialog chrome.
 */
@Injectable({ providedIn: "root" })
export class BvDialogService {
  private readonly openRefs: BvDialogRef[] = [];

  constructor(
    private readonly overlay: Overlay,
    private readonly injector: Injector
  ) {}

  open<T, D = any, R = any>(
    component: ComponentType<T>,
    config: BvDialogConfig<D> = {}
  ): BvDialogRef<T, R> {
    const overlayRef = this.overlay.create(this.buildOverlayConfig(config));
    const dialogRef = new BvDialogRef<T, R>(overlayRef);

    const injector = Injector.create({
      parent: this.injector,
      providers: [
        { provide: BvDialogRef, useValue: dialogRef },
        { provide: BV_DIALOG_DATA, useValue: config.data ?? null },
      ],
    });

    const containerRef = overlayRef.attach(
      new ComponentPortal(BvDialogContainerComponent, null, injector)
    );
    containerRef.instance.ariaLabel = config.ariaLabel;
    containerRef.instance.autoFocus = config.autoFocus !== false;

    const contentRef = containerRef.instance.attachComponentPortal(
      new ComponentPortal(component, null, injector)
    );
    dialogRef.componentInstance = contentRef.instance;

    if (!config.disableClose) {
      overlayRef.backdropClick().subscribe(() => dialogRef.close());
      overlayRef.keydownEvents().subscribe((event: KeyboardEvent) => {
        if (event.key === "Escape") {
          event.preventDefault();
          dialogRef.close();
        }
      });
    }

    this.openRefs.push(dialogRef);
    dialogRef.afterClosed().subscribe(() => {
      const i = this.openRefs.indexOf(dialogRef);
      if (i > -1) this.openRefs.splice(i, 1);
    });

    return dialogRef;
  }

  closeAll(): void {
    [...this.openRefs].forEach((ref) => ref.close());
  }

  private buildOverlayConfig(config: BvDialogConfig): OverlayConfig {
    const panelClass = ["bv-dialog-panel"];
    if (config.panelClass) {
      panelClass.push(
        ...(Array.isArray(config.panelClass)
          ? config.panelClass
          : [config.panelClass])
      );
    }

    return new OverlayConfig({
      hasBackdrop: config.hasBackdrop !== false,
      backdropClass: config.backdropClass ?? "bv-dialog-backdrop",
      panelClass,
      positionStrategy: this.buildPositionStrategy(config),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      width: config.width,
      height: config.height,
      minWidth: config.minWidth,
      maxWidth: config.maxWidth ?? "95vw",
      maxHeight: config.maxHeight ?? "95dvh",
      disposeOnNavigation: true,
    });
  }

  private buildPositionStrategy(config: BvDialogConfig): GlobalPositionStrategy {
    const strategy = this.overlay.position().global();
    const pos = config.position;
    if (!pos) {
      return strategy.centerHorizontally().centerVertically();
    }
    if (pos.top != null) strategy.top(pos.top);
    if (pos.bottom != null) strategy.bottom(pos.bottom);
    if (pos.left != null) strategy.left(pos.left);
    if (pos.right != null) strategy.right(pos.right);
    if (pos.top == null && pos.bottom == null) strategy.centerVertically();
    if (pos.left == null && pos.right == null) strategy.centerHorizontally();
    return strategy;
  }
}
